import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Paper,
  Alert,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Lock as LockIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useAppDispatch } from "../../hooks/hooks";
import { updateUserPassword } from "../../redux/userSlice";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

const SecuritySection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [emailVerified, setEmailVerified] = useState(true);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const dispatch = useAppDispatch();
  const [activeSessions, setActiveSessions] = useState([
    {
      id: "1",
      device: "Chrome on Windows",
      location: "New York, US",
      lastActive: "2 hours ago",
    },
    {
      id: "2",
      device: "Safari on iPhone",
      location: "London, UK",
      lastActive: "5 days ago",
    },
  ]);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitPassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    await dispatch(
      updateUserPassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      })
    );
    setSuccess("Password changed successfully");
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowPasswordForm(false);
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleTerminateSession = (id: string) => {
    setActiveSessions(activeSessions.filter((session) => session.id !== id));
  };

  const scrollableBoxStyles = {
    maxHeight: isMobile ? "50vh" : "60vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    WebkitOverflowScrolling: "touch",
  };

  return (
    <Paper
      sx={{
        p: isMobile ? 2 : 3,
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        ...(isMobile && {
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "none",
        }),
        ...(!isMobile && {
          borderRadius: 2,
          boxShadow: 3,
        }),
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontSize: isMobile ? "1.25rem" : "1.5rem" }}
      >
        Security Settings
      </Typography>
      <Divider sx={{ my: 2 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={isMobile ? 1 : 3} sx={scrollableBoxStyles}>
        {/* Password Change Section */}
        <Grid>
          <Paper elevation={isMobile ? 0 : 2} sx={{ p: isMobile ? 2 : 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="subtitle1"
                sx={{ fontSize: isMobile ? "0.875rem" : "1rem" }}
              >
                <LockIcon
                  sx={{
                    mr: 1,
                    verticalAlign: "middle",
                    fontSize: isMobile ? "1rem" : "1.25rem",
                  }}
                />
                Password
              </Typography>
              {!showPasswordForm && (
                <Button
                  variant="outlined"
                  startIcon={
                    <EditIcon fontSize={isMobile ? "small" : "medium"} />
                  }
                  onClick={() => setShowPasswordForm(true)}
                  size={isMobile ? "small" : "medium"}
                >
                  Change
                </Button>
              )}
            </Box>

            {showPasswordForm && (
              <Box mt={2}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Current Password"
                  name="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  size={isMobile ? "small" : "medium"}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  size={isMobile ? "small" : "medium"}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Confirm New Password"
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  size={isMobile ? "small" : "medium"}
                />
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    sx={{ mr: 1 }}
                    onClick={() => setShowPasswordForm(false)}
                    size={isMobile ? "small" : "medium"}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitPassword}
                    size={isMobile ? "small" : "medium"}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Two-Factor Authentication */}
        <Grid>
          <Paper elevation={isMobile ? 0 : 2} sx={{ p: isMobile ? 2 : 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontSize: isMobile ? "0.875rem" : "1rem" }}
            >
              <LockIcon
                sx={{
                  mr: 1,
                  verticalAlign: "middle",
                  fontSize: isMobile ? "1rem" : "1.25rem",
                }}
              />
              Two-Factor Authentication
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={twoFactorEnabled}
                  onChange={() => setTwoFactorEnabled(!twoFactorEnabled)}
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                />
              }
              label={twoFactorEnabled ? "2FA Enabled" : "2FA Disabled"}
              sx={{ mt: 1 }}
            />
            {twoFactorEnabled && (
              <Box mt={2}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                >
                  Authentication methods:
                </Typography>
                <List dense>
                  <ListItem
                    sx={{ px: 0 }}
                    secondaryAction={
                      <Button size={isMobile ? "small" : "medium"}>
                        {emailVerified ? "Change" : "Verify"}
                      </Button>
                    }
                  >
                    <EmailIcon
                      color={emailVerified ? "success" : "action"}
                      sx={{ mr: 2, fontSize: isMobile ? "1rem" : "1.25rem" }}
                    />
                    <ListItemText
                      primary="Email"
                      secondary={emailVerified ? "Verified" : "Not verified"}
                      primaryTypographyProps={{
                        fontSize: isMobile ? "0.875rem" : "1rem",
                      }}
                      secondaryTypographyProps={{
                        fontSize: isMobile ? "0.75rem" : "0.875rem",
                      }}
                    />
                  </ListItem>
                  <ListItem
                    sx={{ px: 0 }}
                    secondaryAction={
                      <Button size={isMobile ? "small" : "medium"}>
                        {phoneVerified ? "Change" : "Add"}
                      </Button>
                    }
                  >
                    <PhoneIcon
                      color={phoneVerified ? "success" : "action"}
                      sx={{ mr: 2, fontSize: isMobile ? "1rem" : "1.25rem" }}
                    />
                    <ListItemText
                      primary="Phone"
                      secondary={phoneVerified ? "Verified" : "Not verified"}
                      primaryTypographyProps={{
                        fontSize: isMobile ? "0.875rem" : "1rem",
                      }}
                      secondaryTypographyProps={{
                        fontSize: isMobile ? "0.75rem" : "0.875rem",
                      }}
                    />
                  </ListItem>
                </List>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Active Sessions */}
        <Grid>
          <Paper elevation={isMobile ? 0 : 2} sx={{ p: isMobile ? 2 : 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontSize: isMobile ? "0.875rem" : "1rem" }}
            >
              Active Sessions
            </Typography>
            <List>
              {activeSessions.map((session) => (
                <ListItem
                  key={session.id}
                  sx={{ px: 0 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleTerminateSession(session.id)}
                      size={isMobile ? "small" : "medium"}
                    >
                      <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  }
                >
                  <CheckCircleIcon
                    color="success"
                    sx={{ mr: 2, fontSize: isMobile ? "1rem" : "1.25rem" }}
                  />
                  <ListItemText
                    primary={session.device}
                    secondary={`${session.location} • Last active ${session.lastActive}`}
                    primaryTypographyProps={{
                      fontSize: isMobile ? "0.875rem" : "1rem",
                    }}
                    secondaryTypographyProps={{
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                    }}
                  />
                </ListItem>
              ))}
            </List>
            <Button
              variant="text"
              color="error"
              sx={{ mt: 1 }}
              size={isMobile ? "small" : "medium"}
            >
              Terminate all other sessions
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SecuritySection;
